import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	ColorPicker,
	isReadable,
	readableTextColor,
	useAutoTransitioningColors,
	useBuiltInColorMap,
} from '@atlassian/color-picker';
import { CornerStyle, useProductDependencies } from '@atlassian/company-hub-common';
import { CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';
import {
	ELEMENT_DEFAULTS,
	resolveAdfColorForPicker,
	toAdfColor,
} from '@atlassian/company-hub-common/hub-brand-palette';
import {
	HUB_CORNER_RADIUS_MAX,
	HUB_CORNER_RADIUS_MIN,
	HUB_CORNER_RADIUS_STEP,
	resolveCornerRadiusOverride,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { RangeField } from '@atlassian/company-hub-common/range-field';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import type { SpotlightActions, SpotlightParameters } from '../../../../../../types';

import { i18n } from './messages';

const styles = cssMap({
	generalOptionStyles: {
		width: '100%',
	},
});

export type GeneralOptionProps = {
	actions: SpotlightActions;
	parameters: SpotlightParameters;
};

// TODO: COMPHUB-3164 - replace this background color field with the reusable one once it's implemented. Refer to ticket for more context
export const GeneralOptions = ({ parameters, actions }: GeneralOptionProps): React.JSX.Element => {
	const { isSSRProductInDarkMode, isCompanyHub, branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { formatMessage } = useIntl();
	const {
		backgroundColor: backgroundColorAdf,
		content,
		cornerStyle,
		cornerRadius,
	} = parameters ?? {};
	const { updateContentTextColor, updateBackgroundColor, updateCornerStyle, updateCornerRadius } =
		actions;
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	// Per-element corner radius is Premium, so an un-entitled hub gets no control
	// at all rather than one it could not publish. Scoped to the new flow: the
	// legacy rounded/squared toggle below is not a radius setting and stays.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();
	const { backgroundColorMap, emptyColorMap, textColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	// useHubTheming returns undefined in cross-host trees (Confluence Company
	// Hub) that don't mount <HubThemingProvider>. The narrowing below treats
	// "no theming" as gate-OFF — the v2 picker code paths only run when theming
	// is present AND the gate is enabled, so `palette` is always defined inside
	// those branches.
	const theming = useHubTheming();
	const isAdvancedThemingEnabled = !!theming?.isAdvancedThemingEnabled;
	// Picker path: resolve through the JS palette so the indicator hex matches
	// one of the picker's `knownColors` (it can't reverse-lookup CSS-var refs).
	// When the gate is OFF, pass the ADF value straight to the picker — matches
	// pre-v2 behaviour.
	const backgroundColor =
		isAdvancedThemingEnabled && theming
			? resolveAdfColorForPicker(
					backgroundColorAdf,
					theming.palette,
					ELEMENT_DEFAULTS['spotlight.bg'],
					theming.primaryColor,
				)
			: backgroundColorAdf;
	const textColor = content?.text?.textColor;
	const isReadableBackgroundColor = (color: string) => isReadable(color, textColor);
	const isReadableTextColor = (color: string) => isReadable(color, backgroundColor);

	useAutoTransitioningColors(
		backgroundColor,
		isReadableTextColor,
		textColor,
		updateContentTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex testId="general-options" direction="column" xcss={cx(styles.generalOptionStyles)}>
			<Stack space="space.200">
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...i18n.backgroundColor} />
					</Text>
					<ColorPicker
						builtInColorMap={
							hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap
						}
						isReadableColor={isReadableBackgroundColor}
						onChange={(bgColor) => {
							if (!bgColor) return;
							// When v2 is on, persist as a PaletteRef when the picked
							// hex matches a brand-palette shade so it tracks future
							// brand-color edits. Otherwise persist whatever the picker
							// emitted — matches pre-v2 behaviour exactly.
							updateBackgroundColor(
								isAdvancedThemingEnabled && theming
									? toAdfColor(bgColor, theming.palette)
									: bgColor,
							);
						}}
						value={backgroundColor}
						pickerTitle={i18n.backgroundColor}
						validationSuccessMessage={formatMessage(i18n.colorValidationSuccessMessage)}
						validationFailMessage={formatMessage(i18n.colorValidationFailMessage)}
						showCustomPalette={isCompanyHub}
						analyticsSource="spotlightExtensionConfigBackgroundColor"
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...i18n.textColor} />
					</Text>
					<ColorPicker
						builtInColorMap={
							isAdvancedThemingEnabled && theming?.brandBackgroundColorMap
								? theming.brandBackgroundColorMap
								: hideDefaultColorPalette
									? emptyColorMap
									: textColorMap
						}
						isReadableColor={isReadableTextColor}
						onChange={(color) => {
							if (!color) return;
							updateContentTextColor(
								isAdvancedThemingEnabled && theming ? toAdfColor(color, theming.palette) : color,
							);
						}}
						value={
							isAdvancedThemingEnabled && theming
								? resolveAdfColorForPicker(
										textColor,
										theming.palette,
										ELEMENT_DEFAULTS['spotlight.bg'],
										readableTextColor(theming.primaryColor, '@atlassian/company-hub-common'),
									)
								: textColor
						}
						pickerTitle={i18n.textColor}
						validationSuccessMessage={formatMessage(i18n.colorValidationSuccessMessage)}
						validationFailMessage={formatMessage(i18n.colorValidationFailMessage)}
						showCustomPalette={isCompanyHub}
						analyticsSource="spotlightExtensionConfigTextColor"
						showTextIcon
					/>
				</Inline>
				{/* The new-flow slider is Premium-only; the legacy toggle below is not a
				    radius setting, so it is left alone. */}
				{isCompanyHub && isCornerRadiusEnabled && !isCornerRadiusLocked && (
					<RangeField
						label={i18n.cornerRadius}
						minValue={HUB_CORNER_RADIUS_MIN}
						maxValue={HUB_CORNER_RADIUS_MAX}
						step={HUB_CORNER_RADIUS_STEP}
						value={resolveHubCornerRadius(
							resolveCornerRadiusOverride(cornerRadius, cornerStyle) ?? theming?.cornerRadius,
						)}
						updateRangeValue={updateCornerRadius}
						analyticSource="spotlightExtensionConfigCornerRadius"
						prefix="cornerRadius"
						unit="px"
					/>
				)}
				{isCompanyHub && !isCornerRadiusEnabled && (
					<CornerStyleConfig<CornerStyle>
						cornerStyleValue={cornerStyle}
						updateCornerStyle={updateCornerStyle}
						roundCornerValue={CornerStyle.ROUNDED}
						squareCornerValue={CornerStyle.SQUARED}
					/>
				)}
			</Stack>
		</Flex>
	);
};
